import { useNavigate } from '@tanstack/react-router';
import type { InventoryItem } from '@siliconvault/shared';
import { useTranslation } from '../../i18n';
import { usePlatformLabel } from '../../hooks/usePlatformLabel';
import styles from './ItemTable.module.css';

interface ItemTableProps {
  items: InventoryItem[];
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (column: string) => void;
  showOwner?: boolean;
  from?: string;
}

export function ItemTable({ items, sortBy, sortOrder, onSort, showOwner, from }: ItemTableProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const platformLabel = usePlatformLabel();

  const columns = [
    { key: 'name', label: t('itemTable.name') },
    { key: 'platform', label: platformLabel },
    { key: 'category', label: t('itemTable.category') },
    { key: 'subcategory', label: t('itemTable.subcategory') },
    { key: 'videoSystem', label: t('itemForm.videoSystem') },
    { key: 'condition', label: t('itemTable.condition') },
    { key: 'quantity', label: t('itemTable.qty') },
    { key: 'listingStatus', label: t('itemTable.status') },
    { key: 'createdAt', label: t('itemTable.added') },
  ] as const;

  if (items.length === 0) {
    return (
      <div className={styles.empty}>
        {t('itemTable.empty')}
      </div>
    );
  }

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} onClick={() => onSort(col.key)}>
                {col.label}
                {sortBy === col.key && (
                  <span className={styles.sortIndicator}>
                    {sortOrder === 'asc' ? '\u25B2' : '\u25BC'}
                  </span>
                )}
              </th>
            ))}
            {showOwner && <th>{t('itemTable.owner')}</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.id}
              onClick={() => navigate({ to: '/inventory/$itemId', params: { itemId: item.id }, search: from ? { from } : { from: undefined } })}
            >
              <td>{item.name}</td>
              <td>{item.platform}</td>
              <td>{item.category}</td>
              <td>{item.subcategory ?? '—'}</td>
              <td>{item.videoSystem ? t(`videoSystem.${item.videoSystem}` as any) : '—'}</td>
              <td>
                <span className={styles.conditionBadge}>{t(`condition.${item.condition}` as any) || item.condition}</span>
              </td>
              <td>{item.quantity}</td>
              <td>
                {item.listingStatus && item.listingStatus !== 'personal' ? (
                  <span className={item.listingStatus === 'for-trade' ? styles.statusTrade : styles.statusSale}>
                    {item.listingStatus === 'for-trade' ? t('community.statusForTrade') : t('community.statusForSale')}
                  </span>
                ) : (
                  <span className={styles.statusPersonal}>{t('community.statusPersonal')}</span>
                )}
              </td>
              <td>{new Date(item.createdAt).toLocaleDateString()}</td>
              {showOwner && <td>{item.ownerDisplayName ?? '-'}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
